import i18n from 'i18next';
import { formatCurrency, formatDate, formatDistance, formatNumber, formatTime, formatRelative } from './utils';
import { createIntl } from '@formatjs/intl';
import {
  defaultLocale,
  formats,
  supportedLocales,
  supportedLanguages,
  defaultLanguage,
  additionalSupportedLanguages,
  additionalSupportedLocales
} from './constants';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import { isExtendedLocalesEnabled, isI18nFeatureEnabled } from './helpers/featureFlags';
import { buildCustomLanguageDetector } from './helpers/languageDetector';
import { buildIntlConfig, setIntl } from './intl';

const updateLocale = (value: string) => {
  i18n.changeLanguage(value);
};

function camelize(str: string) {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '');
}

function getNamespace() {
  let appName = process.env.REACT_APP_NAME;
  if (!appName) return '';

  const appNamePrefix = 'aion-';
  const appNamePostfix = '-ui';
  if (appName.startsWith(appNamePrefix)) {
    appName = appName.split(appNamePrefix)[1];
  }
  if (appName.endsWith(appNamePostfix)) {
    appName = appName.split(appNamePostfix)[0];
  }
  return camelize(appName.split('-').join(' '));
}

const getCurrentLanguage = () => {
  return i18n.language;
};

const customLanguageDetector = buildCustomLanguageDetector();

let i18nInitialized = () => {};
const i18nInitializationPromise = new Promise(resolve => {
  i18nInitialized = resolve;
});

i18n
  .use(HttpApi)
  .use(customLanguageDetector)
  .use(initReactI18next)
  .init({
    detection: {
      order: ['customDetector']
    },
    backend: {
      loadPath: `${process.env.PUBLIC_URL || `/${getNamespace()}`}/static/locales/{{lng}}/{{ns}}.json`
    },
    load: 'languageOnly',
    fallbackLng: (code = defaultLocale) => {
      const [language] = code.split('-');
      return [language, defaultLanguage];
    },
    supportedLngs: [
      ...supportedLocales,
      ...additionalSupportedLocales,
      ...supportedLanguages,
      ...additionalSupportedLanguages
    ],
    debug: false,
    react: {
      useSuspense: false
    },
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
      format: function(value: string, format: string) {
        switch (format) {
          case formats.currency:
            return formatCurrency(value);
          case formats.dateCustom:
          case formats.dateFull:
          case formats.dateLong:
          case formats.dateMedium:
          case formats.dateShort:
            return formatDate(value, format);
          case formats.distance:
            return formatDistance(value);
          case formats.number:
            return formatNumber(value);
          case formats.timeMedium:
          case formats.timeShort:
            return formatTime(value, format);
          case formats.relativeLong:
          case formats.relativeShort:
          case formats.relativeNarrow:
            return formatRelative(value, format);
          default:
            return value;
        }
      }
    }
  });

i18n.loadNamespaces(getNamespace(), err => {
  if (err) {
    console.log(err);
  }
});

i18n.on('initialized', () => {
  i18nInitialized();
});

Promise.all([isI18nFeatureEnabled(), isExtendedLocalesEnabled(), i18nInitializationPromise]).then(results => {
  const [i18nEnabled, extendedLocalesEnabled] = results;

  // If extended locales is disabled, remove additional locales from supportedLngs
  if (!extendedLocalesEnabled) {
    const additionalLocales = [...additionalSupportedLanguages, ...additionalSupportedLocales];
    const currentSupportedLngs = (i18n.options.supportedLngs as string[]) || [];
    i18n.options.supportedLngs = currentSupportedLngs.filter(lng => !additionalLocales.includes(lng));

    // If current language is an additional locale, reset to default
    if (additionalLocales.includes(i18n.language)) {
      updateLocale(defaultLocale);
    }
  }

  if (!i18nEnabled) updateLocale(defaultLocale);
});

i18n.on('languageChanged', (locale: string | undefined) => {
  setIntl(createIntl(buildIntlConfig(locale)));
});

export { getAvailableLanguages } from './helpers/languages';
export {
  applyLocalePreference,
  shouldAutoApplyLocaleOnCountryChange,
  getSupportedLocale,
  checkLocale
} from './helpers/localePreference';
export { getNamespace, getCurrentLanguage };
